import { Theme } from '@radix-ui/themes'
import { Alert, List, message, Skeleton, Tag, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  createClassificationIndicationConfigContextValue,
  DEFAULT_CLASSIFICATION_CONFIG,
  IndicationClassificationConfigContext,
  IndicationItem,
  indicationToEntry,
  RolodexIndication,
  RolodexIndicationContextProvider,
  ToggledIndicationDetailView,
  UpdateIndicationContext,
} from 'features/indicationTriage'
import {
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import {
  FfsPart,
  FfsResultType,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { computeFfsPartValidation } from 'features/libra/stepComponents/ffs/computeFfsPartValidation'
import {
  DEFAULT_FFS_LTA_DETAIL_CARDS,
  normalizeFfsDetailCards,
  normalizeFfsDetailCardsForPart,
} from 'features/libra/stepComponents/ffs/ffsDetailCards'
import { FFS_INDICATION_LAYER_PREFIX } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { useFfsMarkupLayers } from 'features/libra/stepComponents/ffs/useFfsMarkupLayers'
import { useFfsPartReports } from 'features/libra/stepComponents/ffs/useFfsPartReports'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsTriageData } from 'features/libra/stepComponents/ffs/useFfsTriageData'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

// A stored classification is the governing report's `outcome.result`
// (`FfsResultType`, incl. `skipped`) or a literal like `UNKNOWN`. Resolve its
// tag color/label from the canonical outcome maps, falling back for any value
// outside `FfsResultType` -- no unsound cast.
const isFfsResultType = (value: string): value is FfsResultType =>
  value in RESULT_OUTCOME_COLORS

const outcomeTag = (
  result: string
): { color: string | undefined; label: string } =>
  isFfsResultType(result)
    ? {
        color: RESULT_OUTCOME_COLORS[result],
        label: RESULT_OUTCOME_LABELS[result],
      }
    : { color: undefined, label: result }

const PART_META: Record<FfsPart, { title: string; blurb: string }> = {
  [FfsPart.PART_4]: {
    title: 'FFS Part 4 — General Corrosion',
    blurb:
      'Run the Part 4 (general metal loss) assessment for each LTA. Level 2 unlocks once Level 1 is complete.',
  },
  [FfsPart.PART_5]: {
    title: 'FFS Part 5 — Local Metal Loss',
    blurb:
      'Run the Part 5 (local metal loss) assessment for each LTA. Level 2 unlocks once Level 1 is complete.',
  },
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

interface FfsPartStepProps {
  ffsPart: FfsPart
  readonly?: boolean
}

/**
 * Shared core for the part-scoped FFS steps (Part 4 / Part 5). Lists the LTA
 * indications and opens the inline `ToggledIndicationDetailView`, whose FFS
 * calc card is PINNED to `ffsPart` (via `normalizeFfsDetailCardsForPart`) so it
 * shows only that part's Level 1 / Level 2 (with per-LTA level locking).
 *
 * Split out of the former `FfsIndicationTriageStep`. The manual classify
 * buttons are gone: classification is derived from the governing FFS report.
 */
export const FfsPartStep: FC<FfsPartStepProps> = ({
  ffsPart,
  readonly = false,
}) => {
  const readOnly = readonly
  const {
    component,
    componentId,
    inspectionId,
    componentModel,
    assetModel,
    isLoading: ticketLoading,
  } = useFfsTicketContext()
  const {
    ltaSet,
    indications,
    isLoading: triageDataLoading,
    forceRefresh: refreshTriageIndications,
  } = useFfsTriageData({ component, inspectionId })

  const indicationIds = useMemo(
    () => indications.map((indication) => indication.id),
    [indications]
  )
  const {
    indicationIdsWithReport,
    isLoading: partReportsLoading,
    forceRefresh: refreshPartReports,
  } = useFfsPartReports(indicationIds, ffsPart)

  const isLoading = ticketLoading || triageDataLoading || partReportsLoading

  const { validation, eligibleCount, assessedCount } = useMemo(
    () =>
      computeFfsPartValidation({
        part: ffsPart,
        componentId,
        inspectionId,
        component,
        componentModel,
        ltaSet,
        indications,
        indicationIdsWithReport,
        isLoading,
      }),
    [
      ffsPart,
      component,
      componentId,
      inspectionId,
      componentModel,
      ltaSet,
      indications,
      indicationIdsWithReport,
      isLoading,
    ]
  )

  useReportValidation(validation)

  const [messageApi, messageContextHolder] = message.useMessage()
  const { rolodexPut } = useRolodexEdit()
  const {
    selectedIndicationId,
    setSelectedIndicationId,
    primaryCdf,
    setZoomToLayerId,
  } = useFfsWorkspaceState()

  useFfsMarkupLayers({
    indications,
    ltaSetId: ltaSet?.id,
    component,
    inspectionId,
    selectedIndicationId,
  })

  const [detailIndication, setDetailIndication] =
    useState<RolodexIndication | null>(null)

  // Detail view <-> viz selection sync (same contract as the old triage step).
  useEffect(() => {
    if (!selectedIndicationId) {
      if (detailIndication) setDetailIndication(null)
      return
    }
    if (detailIndication?.id === selectedIndicationId) return
    const match = indications.find((i) => i.id === selectedIndicationId)
    if (match) setDetailIndication(match)
  }, [selectedIndicationId, indications, detailIndication])

  const classificationFor = (
    indication: RolodexIndication
  ): string | undefined => indication.classificationInfo?.classification

  const classificationConfigValue = useMemo(
    () =>
      createClassificationIndicationConfigContextValue(
        DEFAULT_CLASSIFICATION_CONFIG
      ),
    []
  )

  const updateContextValue = useMemo(
    () => ({
      updateOrAddIndication: (item: IndicationItem) => {
        // On a read-only ticket refuse drawer-driven writes.
        if (readOnly) {
          messageApi.warning(
            'Ticket is read-only; changes were not saved to Rolodex.'
          )
          return
        }
        const rolodexItem = item as RolodexIndication
        const baseEntry = indicationToEntry(rolodexItem)
        const classificationInfo = rolodexItem.classificationInfo
          ? {
              classification: rolodexItem.classificationInfo.classification,
              refVal: rolodexItem.classificationInfo.refVal?.num,
              unitId: rolodexItem.classificationInfo.refVal?.unit.id,
            }
          : undefined
        void (async () => {
          try {
            await rolodexPut([
              {
                ...baseEntry,
                data: {
                  ...baseEntry.data,
                  ...(classificationInfo ? { classificationInfo } : {}),
                },
              },
            ])
            await refreshTriageIndications()
            await refreshPartReports()
          } catch {
            messageApi.error(`Failed to update ${rolodexItem.id}`)
          }
        })()
      },
      // Refresh BOTH the indication query and this step's part-completion query
      // -- report saves in the calc card call this, and completion must react.
      refreshIndications: async () => {
        await refreshTriageIndications()
        await refreshPartReports()
      },
    }),
    [
      messageApi,
      readOnly,
      refreshTriageIndications,
      refreshPartReports,
      rolodexPut,
    ]
  )

  const resolvedDetailCards = useMemo(() => {
    const base = normalizeFfsDetailCards(
      ltaSet?.detailCards?.length
        ? ltaSet.detailCards
        : DEFAULT_FFS_LTA_DETAIL_CARDS
    )
    return normalizeFfsDetailCardsForPart(base, ffsPart)
  }, [ltaSet?.detailCards, ffsPart])

  const detailExtensionContext = useMemo(
    () => ({
      bottomPanelContainer: null,
      requestAuxiliaryBottomPanel: () => {},
      releaseAuxiliaryBottomPanel: () => {},
      zoomToRegion: (regionId: string) => setZoomToLayerId(regionId),
    }),
    [setZoomToLayerId]
  )

  const meta = PART_META[ffsPart]

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  return (
    <Container>
      {messageContextHolder}
      <Typography.Title level={4}>{meta.title}</Typography.Title>
      <Typography.Paragraph type='secondary'>
        {meta.blurb} {assessedCount} of {eligibleCount} assessed.
      </Typography.Paragraph>

      {!componentId || !inspectionId ? (
        <Alert
          type='error'
          showIcon
          message='Ticket is missing required component or inspection tag.'
        />
      ) : !component ? (
        <Alert
          type='error'
          showIcon
          message='Component entry not found in Rolodex.'
        />
      ) : !ltaSet ? (
        <Alert
          type='warning'
          showIcon
          message='No LTA indication set found yet. Return to the LTA Generation step and generate Local Thin Areas first.'
        />
      ) : indications.length === 0 ? (
        <Alert
          type='info'
          showIcon
          message={`Indication set "${ltaSet.id}" exists but has no indications.`}
        />
      ) : (
        <List
          size='small'
          bordered
          dataSource={[...indications]}
          renderItem={(indication: RolodexIndication) => {
            const current = classificationFor(indication)
            const hasReport = indicationIdsWithReport.has(indication.id)
            return (
              <List.Item
                onClick={() => {
                  setSelectedIndicationId(indication.id)
                  setDetailIndication(indication)
                  // Frame the viz on this LTA so the engineer doesn't have to
                  // hunt for it. The one-shot effect in FfsAssetVizPanel resolves
                  // the prefixed layer id to bounds and zooms, then clears it.
                  setZoomToLayerId(
                    `${FFS_INDICATION_LAYER_PREFIX}${indication.id}`
                  )
                }}
                style={{ cursor: 'pointer' }}>
                <Typography.Link>{indication.title}</Typography.Link>
                {current ? (
                  <Tag color={outcomeTag(current).color}>
                    {outcomeTag(current).label}
                  </Tag>
                ) : hasReport ? (
                  <Tag color='blue'>assessed</Tag>
                ) : (
                  <Tag>not started</Tag>
                )}
              </List.Item>
            )
          }}
        />
      )}

      <Theme asChild hasBackground={false} appearance='inherit'>
        <IndicationClassificationConfigContext.Provider
          value={classificationConfigValue}>
          <UpdateIndicationContext.Provider value={updateContextValue}>
            {detailIndication !== null ? (
              <RolodexIndicationContextProvider
                indicationId={detailIndication.id}
                resolvedThicknessCdf={primaryCdf}
                resolvedAssetModel={assetModel}>
                <ToggledIndicationDetailView
                  isOpen={detailIndication !== null}
                  close={() => {
                    setDetailIndication(null)
                    setSelectedIndicationId(null)
                  }}
                  indicationItem={detailIndication}
                  cardConfig={resolvedDetailCards}
                  extensionContext={detailExtensionContext}
                  type='inline'
                />
              </RolodexIndicationContextProvider>
            ) : null}
          </UpdateIndicationContext.Provider>
        </IndicationClassificationConfigContext.Provider>
      </Theme>
    </Container>
  )
}
